import { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Clock, Loader2, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! Our team will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message");
    }
    setSending(false);
  };

  const phone = settings?.phone || "(407) 000-0000";
  const email = settings?.email || "FlavorsNigeria@pm.me";
  const address = settings?.address || "Orlando, FL";

  return (
    <div className="mx-auto max-w-6xl px-6 py-14">
      <h1 className="font-display text-4xl font-extrabold text-stone-900 sm:text-5xl">Contact & Customer Care</h1>
      <p className="mt-3 max-w-2xl text-stone-600">Questions about your order, catering, or feedback? We'd love to hear from you.</p>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        {/* Info */}
        <div className="space-y-6">
          <div className="space-y-4 rounded-2xl border border-stone-200 bg-white p-6">
            <div className="flex items-start gap-3"><MapPin className="mt-1 h-5 w-5 text-orange-700" /><div><div className="font-semibold text-stone-900">Location</div><div className="text-stone-600">{address}</div></div></div>
            <div className="flex items-start gap-3"><Phone className="mt-1 h-5 w-5 text-orange-700" /><div><div className="font-semibold text-stone-900">Phone</div><a href={`tel:${phone.replace(/[^0-9+]/g, "")}`} className="text-stone-600 hover:text-orange-700">{phone}</a></div></div>
            <div className="flex items-start gap-3"><Mail className="mt-1 h-5 w-5 text-orange-700" /><div><div className="font-semibold text-stone-900">Email</div><a href={`mailto:${email}`} className="text-stone-600 hover:text-orange-700">{email}</a></div></div>
            <div className="flex items-start gap-3"><Clock className="mt-1 h-5 w-5 text-orange-700" /><div><div className="font-semibold text-stone-900">Hours</div><div className="text-stone-600">Mon–Sat · 11:00am – 7:00pm<br />Sunday · 1:00pm – 6:00pm</div></div></div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-stone-200">
            <iframe title="map" src="https://www.google.com/maps?q=3530+S+Orange+Ave,+Orlando,+FL+32806&output=embed" className="h-64 w-full border-0" loading="lazy" />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="h-fit rounded-2xl border border-stone-200 bg-white p-6" data-testid="contact-form">
          <h2 className="font-display text-2xl font-bold text-stone-900">Send us a message</h2>
          <div className="mt-4 space-y-4">
            <div><Label htmlFor="c-name">Name</Label><Input id="c-name" data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Your name" /></div>
            <div><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="you@email.com" /></div>
            <div><Label htmlFor="c-msg">Message</Label><Textarea id="c-msg" data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 resize-none" rows={5} placeholder="How can we help?" /></div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-orange-700 py-3.5 font-bold text-white transition-colors duration-200 hover:bg-orange-800 disabled:bg-stone-300"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send Message</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
